import { useEffect, useState, useRef } from "react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import styles from "./WorkOrderCreate.module.scss";
import classNames from "classnames/bind";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import { PlusIcon, SearchIcon, ArrowIcon } from "~/assets/icons";
import userManagementApi, { IEmployee } from "~/apis/userManagement";
import { XIcon } from "lucide-react";
import jobDetailsApi from "~/apis/jobDetails";
import { notify } from "~/utils/helper";
import dayjs from "dayjs";
import { IImages, IAttachments } from "~/utils/interface/jobDetails";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";

const cx = classNames.bind(styles);

interface WorkOrderCreateProps {
  jobId: string;
  type: "create" | "edit";
  onSuccess: () => void;
  onCancel?: () => void;
  workOrderId?: string | null;
}

interface LaborItem {
  id: string;
  perSquare: string;
  uom: string;
  qty: string;
  price: string;
  markup: string;
}

interface FormData {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  type: string;
  status: string;
  priority: string;
  estimatedDuration: string;
  dueDate: string;
  specialInstructions: string;
  internalNote: string;
}

interface FormErrors {
  [key: string]: string;
}

const WorkOrderCreate = ({
  jobId,
  type,
  onCancel,
  onSuccess,
  workOrderId,
}: WorkOrderCreateProps) => {
  const [formData, setFormData] = useState<FormData>({
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    type: "",
    status: "",
    priority: "",
    estimatedDuration: "",
    dueDate: "",
    specialInstructions: "",
    internalNote: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [images, setImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<IImages[]>([]);
  const [deletedImageIds, setDeletedImageIds] = useState<number[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<
    IAttachments[]
  >([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<number[]>(
    []
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  const [laborItems, setLaborItems] = useState<LaborItem[]>([
    {
      id: "1",
      perSquare: "",
      uom: "",
      qty: "",
      price: "",
      markup: "",
    },
  ]);
  const [employees, setEmployees] = useState<IEmployee[]>([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const loadingCountRef = useRef(0);

  const setLoading = (loading: boolean) => {
    if (loading) {
      loadingCountRef.current += 1;
      setIsLoading(true);
    } else {
      loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
      if (loadingCountRef.current === 0) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const fetchWorkOrderData = async () => {
      if (type === "edit" && workOrderId) {
        setLoading(true);
        try {
          const response = await jobDetailsApi.getOverviewWorkOrderDetails(
            workOrderId
          );
          const data = response.data?.data;

          if (data) {
            const combineDateTime = (dateStr: string, timeStr: string) => {
              if (!dateStr || !timeStr) return "";

              const datePart = dateStr.split("T")[0];
              const timePart = timeStr.split(/[+]/)[0];

              const localDate = new Date(`${datePart}T${timePart}`);

              return localDate.toISOString();
            };

            setFormData({
              startDate: data.startDate
                ? dayjs(data.startDate).format("YYYY-MM-DD")
                : "",
              startTime:
                data.startDate && data.startTime
                  ? combineDateTime(data.startDate, data.startTime)
                  : "",
              endDate: data.endDate
                ? dayjs(data.endDate).format("YYYY-MM-DD")
                : "",
              endTime:
                data.endDate && data.endTime
                  ? combineDateTime(data.endDate, data.endTime)
                  : "",
              type: data.type || "",
              status: data.status || "",
              priority: data.priority || "",
              estimatedDuration: data.estimatedDuration?.toString() || "",
              dueDate: data.dueDate
                ? dayjs(data.dueDate).format("YYYY-MM-DD")
                : "",
              specialInstructions: data.specialInstructions || "",
              internalNote: data.internalNote || "",
            });

            // Load labor items
            if (data.laborDetails && data.laborDetails.length > 0) {
              setLaborItems(
                data.laborDetails.map((item: any, index: number) => ({
                  id: (index + 1).toString(),
                  perSquare: item.perSquare || "",
                  uom: item.uom || "",
                  qty: item.qty?.toString() || "",
                  price: item.price?.toString() || "",
                  markup: item.markupPercent?.toString() || "",
                }))
              );
            }

            // Load subcontractors
            if (data.subcontractors && data.subcontractors.length > 0) {
              setSelectedEmployeeIds(
                data.subcontractors.map((sub: any) => sub.accountId)
              );
            }

            // Load existing images
            if (data.images && data.images.length > 0) {
              setExistingImages(data.images);
            }

            // Load existing attachments
            if (data.attachments && data.attachments.length > 0) {
              setExistingAttachments(data.attachments);
            }
          }
        } catch (error: any) {
          notify(
            "error",
            error?.response?.data?.message || "Failed to fetch work order data"
          );
        } finally {
          setLoading(false);
        }
      }
    };

    fetchWorkOrderData();
  }, [type, workOrderId]);

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const response = await userManagementApi.getEmployees();
        setEmployees(response.data?.data?.responses || []);
      } catch (error: any) {
        notify(
          "error",
          error?.response?.data?.message || "Failed to fetch employees"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen && dropdownRef.current && bodyRef.current) {
      setTimeout(() => {
        const dropdownTrigger = dropdownRef.current?.children[0] as HTMLElement;
        let dropdownMenu: HTMLElement | null = null;
        if (dropdownRef.current) {
          for (let i = 0; i < dropdownRef.current.children.length; i++) {
            const child = dropdownRef.current.children[i] as HTMLElement;
            if (child.className && child.className.includes("dropdown-menu")) {
              dropdownMenu = child;
              break;
            }
          }
        }

        if (dropdownTrigger && bodyRef.current) {
          const triggerRect = dropdownTrigger.getBoundingClientRect();
          const bodyRect = bodyRef.current.getBoundingClientRect();
          const padding = 20;

          if (dropdownMenu) {
            const menuHeight = dropdownMenu.offsetHeight || 300;
            const dropdownBottom = triggerRect.bottom + menuHeight;
            const bodyBottom = bodyRect.bottom;

            if (dropdownBottom > bodyBottom) {
              const overflowAmount = dropdownBottom - bodyBottom;
              const scrollAmount = overflowAmount + padding;
              const currentScrollTop = bodyRef.current.scrollTop;
              const newScrollTop = currentScrollTop + scrollAmount;

              bodyRef.current.scrollTo({
                top: newScrollTop,
                behavior: "smooth",
              });

              setTimeout(() => {
                const recheckTriggerRect =
                  dropdownTrigger.getBoundingClientRect();
                const recheckBodyRect =
                  bodyRef.current?.getBoundingClientRect();
                if (recheckBodyRect && dropdownMenu) {
                  const recheckMenuHeight = dropdownMenu.offsetHeight || 300;
                  const recheckDropdownBottom =
                    recheckTriggerRect.bottom + recheckMenuHeight;
                  if (recheckDropdownBottom > recheckBodyRect.bottom) {
                    const additionalScroll =
                      recheckDropdownBottom - recheckBodyRect.bottom + padding;
                    bodyRef.current?.scrollBy({
                      top: additionalScroll,
                      behavior: "smooth",
                    });
                  }
                }
              }, 300);
            } else if (triggerRect.top < bodyRect.top) {
              const overflowAmount = bodyRect.top - triggerRect.top;
              const scrollAmount = overflowAmount + padding;
              const currentScrollTop = bodyRef.current.scrollTop;
              const newScrollTop = Math.max(0, currentScrollTop - scrollAmount);

              bodyRef.current.scrollTo({
                top: newScrollTop,
                behavior: "smooth",
              });

              setTimeout(() => {
                const recheckTriggerRect =
                  dropdownTrigger.getBoundingClientRect();
                const recheckBodyRect =
                  bodyRef.current?.getBoundingClientRect();
                if (
                  recheckBodyRect &&
                  recheckTriggerRect.top < recheckBodyRect.top
                ) {
                  const additionalScroll =
                    recheckBodyRect.top - recheckTriggerRect.top + padding;
                  bodyRef.current?.scrollBy({
                    top: -additionalScroll,
                    behavior: "smooth",
                  });
                }
              }, 300);
            } else {
              dropdownTrigger.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "nearest",
              });
            }
          } else {
            dropdownTrigger.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: "nearest",
            });
          }
        }
      }, 100);
    }
  }, [isDropdownOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleEmployeeToggle = (employeeId: string) => {
    setSelectedEmployeeIds((prev) => {
      if (prev.includes(employeeId)) {
        return prev.filter((id) => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
    if (errors.assignees) {
      setErrors((prev) => ({ ...prev, assignees: "" }));
    }
  };

  const handleDeleteEmployee = (employeeId: string) => {
    setSelectedEmployeeIds((prev) => prev.filter((id) => id !== employeeId));
  };

  const getFilteredEmployees = () => {
    if (!searchQuery.trim()) {
      return employees;
    }

    const query = searchQuery.toLowerCase();
    return employees.filter(
      (employee) =>
        employee?.username?.toLowerCase()?.includes(query) ||
        employee?.email?.toLowerCase()?.includes(query)
    );
  };

  const getSelectedEmployees = () => {
    return employees.filter((employee) =>
      selectedEmployeeIds.includes(employee.id.toString())
    );
  };

  const handleAddItem = () => {
    const newItem: LaborItem = {
      id: Date.now().toString(),
      perSquare: "",
      uom: "",
      qty: "",
      price: "",
      markup: "",
    };
    setLaborItems([...laborItems, newItem]);
  };

  const handleDeleteItem = (id: string) => {
    if (laborItems.length > 1) {
      setLaborItems(laborItems.filter((item) => item.id !== id));
    }
  };

  const handleItemChange = (
    id: string,
    field: keyof LaborItem,
    value: string
  ) => {
    setLaborItems(
      laborItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const calculateLineTotal = (item: LaborItem) => {
    const qty = parseFloat(item.qty) || 0;
    const price = parseFloat(item.price) || 0;
    const markup = parseFloat(item.markup) || 0;
    const subtotal = qty * price;
    const total = subtotal + (subtotal * markup) / 100;
    return total.toFixed(2);
  };

  const calculateTotalMaterialCost = () => {
    return laborItems.reduce((total, item) => {
      return total + parseFloat(calculateLineTotal(item));
    }, 0);
  };

  const calculateTotalLaborCost = () => {
    return 0;
  };

  const calculateMarginAmount = () => {
    if (laborItems.length === 0) return 0;
    const totalMarkup = laborItems.reduce(
      (sum, item) => sum + (parseFloat(item.markup) || 0),
      0
    );
    const avgMarkup = totalMarkup / laborItems.length;
    const totalCost = calculateTotalMaterialCost();
    return (totalCost * avgMarkup) / 100;
  };

  const calculateTotalCost = () => {
    return calculateTotalMaterialCost() + calculateTotalLaborCost();
  };

  const calculateTotalPrice = () => {
    return calculateTotalMaterialCost() + calculateMarginAmount();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setImages((prev) => [...prev, ...newFiles]);
    }
  };

  const handleAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments((prev) => [...prev, ...newFiles]);
    }
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (size: string | number | undefined): string => {
    if (!size) return "";

    let bytes: number;
    if (typeof size === "string") {
      if (size.includes("KB") || size.includes("MB") || size.includes("GB")) {
        return size;
      }
      bytes = parseFloat(size);
    } else {
      bytes = size;
    }

    if (isNaN(bytes) || bytes === 0) return "";

    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const handleRemoveExistingImage = (imageId: string) => {
    const imageIdNum = parseInt(imageId, 10);
    if (!isNaN(imageIdNum)) {
      setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
      setDeletedImageIds((prev) => {
        // Avoid duplicates
        if (!prev.includes(imageIdNum)) {
          return [...prev, imageIdNum];
        }
        return prev;
      });
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingAttachment = (attachmentId: string) => {
    const attachmentIdNum = parseInt(attachmentId, 10);
    if (!isNaN(attachmentIdNum)) {
      setExistingAttachments((prev) =>
        prev.filter((att) => att.id !== attachmentId)
      );
      setDeletedAttachmentIds((prev) => {
        // Avoid duplicates
        if (!prev.includes(attachmentIdNum)) {
          return [...prev, attachmentIdNum];
        }
        return prev;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.endDate) newErrors.endDate = "End date is required";
    if (!formData.endTime) newErrors.endTime = "End time is required";
    if (!formData.type) newErrors.type = "Type is required";
    if (!formData.status) newErrors.status = "Status is required";
    if (!formData.priority) newErrors.priority = "Priority is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";
    if (!formData.startTime) newErrors.startTime = "Start time is required";
    if (!formData.dueDate) newErrors.dueDate = "Due date is required";
    if (!formData.estimatedDuration)
      newErrors.estimatedDuration = "Estimated duration is required";

    // Validate dates
    if (formData.startDate && formData.dueDate) {
      const startDate = new Date(formData.startDate);
      const dueDate = new Date(formData.dueDate);
      if (dueDate < startDate) {
        newErrors.dueDate = "Due date must be after start date";
      }
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (endDate < startDate) {
        newErrors.endDate = "End date must be after start date";
      }
    }

    // Validate numeric fields
    if (
      formData.estimatedDuration &&
      parseFloat(formData.estimatedDuration) <= 0
    ) {
      newErrors.estimatedDuration = "Duration must be greater than 0";
    }

    // Validate assignees
    if (selectedEmployeeIds.length === 0) {
      newErrors.assignees = "At least one assignee is required";
    }

    // Validate labor items
    const hasIncompleteLaborItem = laborItems.some(
      (item) =>
        (item.perSquare && (!item.qty || !item.price)) ||
        (item.qty && (!item.perSquare || !item.price)) ||
        (item.price && (!item.perSquare || !item.qty))
    );

    if (hasIncompleteLaborItem) {
      newErrors.laborItems =
        "Please complete all fields for labor items or remove incomplete items";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const toTimeString = (value: string | Date) => {
    const date = new Date(value);

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
  };

  const prepareFormData = (): globalThis.FormData => {
    const apiFormData = new globalThis.FormData();

    // Add basic fields
    apiFormData.append("jobId", jobId);
    apiFormData.append("type", formData.type);
    apiFormData.append("status", formData.status);
    apiFormData.append("priority", formData.priority);

    // Convert dates to ISO format
    // START
    apiFormData.append("startDate", formData.startDate);
    apiFormData.append(
      "startTime",
      toTimeString(formData.startTime).toString()
    );

    // END
    apiFormData.append("endDate", formData.endDate);
    apiFormData.append("endTime", toTimeString(formData.endTime).toString());

    // DUE
    apiFormData.append("dueDate", formData.dueDate);

    apiFormData.append("estimatedDuration", formData.estimatedDuration);

    if (formData.internalNote) {
      apiFormData.append("internalNote", formData.internalNote);
    }

    if (formData.specialInstructions) {
      apiFormData.append("specialInstructions", formData.specialInstructions);
    }

    // Add labor details
    laborItems.forEach((item, index) => {
      if (item.perSquare && item.qty && item.price) {
        apiFormData.append(`laborDetails[${index}][perSquare]`, item.perSquare);
        apiFormData.append(`laborDetails[${index}][uom]`, item.uom || "");
        apiFormData.append(`laborDetails[${index}][qty]`, item.qty);
        apiFormData.append(`laborDetails[${index}][price]`, item.price);
        apiFormData.append(
          `laborDetails[${index}][markupPercent]`,
          item.markup || "0"
        );
      }
    });

    // Add subcontractor IDs
    selectedEmployeeIds.forEach((id) => {
      apiFormData.append("subcontractorIds", id);
    });

    // Add images
    images.forEach((image) => {
      apiFormData.append("images", image);
    });

    if (deletedImageIds.length > 0) {
      deletedImageIds.forEach((imageId) => {
        apiFormData.append("deleteImageIds", imageId.toString());
      });
    }

    // Add attachments
    attachments.forEach((attachment) => {
      apiFormData.append("attachments", attachment);
    });

    if (deletedAttachmentIds.length > 0) {
      deletedAttachmentIds.forEach((attachmentId) => {
        apiFormData.append("deleteAttachmentIds", attachmentId.toString());
      });
    }

    return apiFormData;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementsByName(firstErrorField)[0];
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const apiFormData = prepareFormData();

      if (type === "create") {
        await jobDetailsApi.createWorkOrder(apiFormData);
        notify("success", "Work order created successfully!");
      } else {
        await jobDetailsApi.updateWorkOrder(workOrderId!, apiFormData);
        notify("success", "Work order updated successfully!");
      }

      onSuccess();
    } catch (error: any) {
      notify(
        "error",
        error?.response?.data?.message ||
        `Failed to ${type === "create" ? "create" : "update"
        } work order. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredEmployees = getFilteredEmployees();
  const selectedEmployees = getSelectedEmployees();

  return (
    <div className={cx("work-order-create-container")}>
      <div className={cx("header")}>
        <h2 className={cx("title")}>
          {type === "create" ? "Create Work Order" : "Edit Work Order"}
        </h2>
      </div>

      {isLoading ? (
        <div className={cx("loading-container")}>
          <LoadingMainContent />
        </div>
      ) : (
        <div className={cx("body")} ref={bodyRef}>
        <div className={cx("section")}>
          <div className={cx("row")}>

            <PrimaryInput
              label="Start Date"
              name="startDate"
              placeholder="Enter start date"
              value={formData.startDate}
              onChange={handleInputChange}
              className={cx("input")}
              type="date"
              errorMessage={errors.startDate}
            />

            <PrimaryInput
              label="Start Time"
              name="startTime"
              placeholder="Enter start time"
              value={formData.startTime}
              onChange={handleInputChange}
              className={cx("input")}
              type="time"
              errorMessage={errors.startTime}
            />
          </div>

          <div className={cx("row")}>
            <PrimaryInput
              label="End Date"
              name="endDate"
              placeholder="Enter end date"
              value={formData.endDate}
              onChange={handleInputChange}
              className={cx("input")}
              type="date"
              errorMessage={errors.endDate}
            />

            <PrimaryInput
              label="End Time"
              name="endTime"
              placeholder="Enter end time"
              value={formData.endTime}
              onChange={handleInputChange}
              className={cx("input")}
              type="time"
              errorMessage={errors.endTime}
            />

            <PrimarySelectInput
              label="Type"
              name="type"
              placeholder="Select type"
              value={formData.type}
              onChange={(value) => handleSelectChange("type", value)}
              className={cx("select-input")}
              options={[
                { label: "Maintenance", value: "Maintenance" },
                { label: "Repair", value: "Repair" },
                { label: "Installation", value: "Installation" },
                { label: "Inspection", value: "Inspection" },
              ]}
              errorMessage={errors.type}
            />
          </div>

          <div className={cx("row")}>
            <PrimarySelectInput
              label="Status"
              name="status"
              placeholder="Select status"
              value={formData.status}
              onChange={(value) => handleSelectChange("status", value)}
              className={cx("select-input")}
              options={[
                { label: "Pending", value: "Pending" },
                { label: "In Progress", value: "In Progress" },
                { label: "Completed", value: "Completed" },
              ]}
              errorMessage={errors.status}
            />

            <PrimarySelectInput
              label="Priority"
              name="priority"
              placeholder="Select priority"
              value={formData.priority}
              onChange={(value) => handleSelectChange("priority", value)}
              className={cx("select-input")}
              options={[
                { label: "Low", value: "Low" },
                { label: "Normal", value: "Normal" },
                { label: "High", value: "High" },
                { label: "Urgent", value: "Urgent" },
              ]}
              errorMessage={errors.priority}
            />

            <PrimaryInput
              label="Estimated Duration (hours)"
              name="estimatedDuration"
              placeholder="Enter estimated duration"
              value={formData.estimatedDuration}
              onChange={handleInputChange}
              className={cx("input")}
              type="number"
              errorMessage={errors.estimatedDuration}
              maxLength={50}
            />
          </div>

          <div className={cx("row")}>
            <PrimaryInput
              label="Due Date"
              name="dueDate"
              placeholder="Enter due date"
              value={formData.dueDate}
              onChange={handleInputChange}
              className={cx("input", "!w-1/2")}
              type="date"
              errorMessage={errors.dueDate}
            />

            <div className={cx("form-group")}>
              <label className={cx("field-label")}>Assign Subcontractors</label>
              <div className={cx("assignees-selector")} ref={dropdownRef}>
                <div
                  className={cx("dropdown-trigger", {
                    open: isDropdownOpen,
                    error: errors.assignees,
                  })}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <div className={cx("selected-display")}>
                    {selectedEmployees.length > 0 ? (
                      <div className={cx("selected-employees")}>
                        {selectedEmployees.map((employee) => (
                          <div
                            key={employee.id}
                            className={cx("selected-employee")}
                          >
                            <span className={cx("employee-name")}>
                              {employee.username}
                            </span>
                            <button
                              className={cx("delete-button")}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteEmployee(employee.id.toString());
                              }}
                            >
                              <XIcon className={cx("delete-icon")} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className={cx("placeholder")}>
                        Select assignee subcontractors...
                      </span>
                    )}
                  </div>
                  <ArrowIcon
                    className={cx("arrow-icon", { rotated: isDropdownOpen })}
                  />
                </div>
                {errors.assignees && (
                  <span className={cx("error-message")}>
                    {errors.assignees}
                  </span>
                )}

                {isDropdownOpen && (
                  <div className={cx("dropdown-menu")}>
                    <div className={cx("search-container")}>
                      <SearchIcon className={cx("search-icon")} />
                      <input
                        type="text"
                        className={cx("search-input")}
                        placeholder="Search employees..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        maxLength={50}
                      />
                    </div>

                    <div className={cx("user-list")}>
                      {filteredEmployees.length > 0 ? (
                        filteredEmployees.map((employee) => {
                          const isSelected = selectedEmployeeIds.includes(
                            employee.id.toString()
                          );
                          return (
                            <label
                              key={employee.id}
                              className={cx("user-checkbox-label", {
                                selected: isSelected,
                              })}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() =>
                                  handleEmployeeToggle(employee.id.toString())
                                }
                                className={cx("user-checkbox")}
                              />
                              <span className={cx("user-name")}>
                                {employee.username}
                              </span>
                            </label>
                          );
                        })
                      ) : (
                        <div className={cx("no-users")}>
                          {searchQuery
                            ? "No employees found"
                            : "No available employees"}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={cx("section")}>
          <div className={cx("labor-header")}>
            <h3 className={cx("labor-title")}>Labor Included</h3>
          </div>

          {errors.laborItems && (
            <div className={cx("error-message", "mb-3")}>
              {errors.laborItems}
            </div>
          )}

          <div className={cx("labor-table-container")}>
            <div className={cx("labor-table")}>
              <div className={cx("table-header")}>
                <div className={cx("table-cell", "per-square")}>Per Square</div>
                <div className={cx("table-cell", "uom")}>UOM</div>
                <div className={cx("table-cell", "qty")}>Qty</div>
                <div className={cx("table-cell", "price")}>Price</div>
                <div className={cx("table-cell", "markup")}>Markup%</div>
                <div className={cx("table-cell", "line-total")}>Line Total</div>
                <div className={cx("table-cell", "action")}></div>
              </div>

              <div className={cx("table-body")}>
                {laborItems.map((item) => (
                  <div key={item.id} className={cx("table-row")}>
                    <div className={cx("table-cell", "per-square")}>
                      <PrimaryInput
                        name={`perSquare-${item.id}`}
                        placeholder="Per Square"
                        value={item.perSquare}
                        onChange={(e) =>
                          handleItemChange(item.id, "perSquare", e.target.value)
                        }
                        className={cx("table-input")}
                        maxLength={50}
                      />
                    </div>
                    <div className={cx("table-cell", "uom")}>
                      <PrimaryInput
                        name={`uom-${item.id}`}
                        placeholder="UOM"
                        value={item.uom}
                        onChange={(e) =>
                          handleItemChange(item.id, "uom", e.target.value)
                        }
                        className={cx("table-input")}
                        maxLength={50}
                      />
                    </div>
                    <div className={cx("table-cell", "qty")}>
                      <PrimaryInput
                        name={`qty-${item.id}`}
                        placeholder="Qty"
                        value={item.qty}
                        onChange={(e) =>
                          handleItemChange(item.id, "qty", e.target.value)
                        }
                        className={cx("table-input")}
                        type="number"
                        maxLength={50}
                      />
                    </div>
                    <div className={cx("table-cell", "price")}>
                      <PrimaryInput
                        name={`price-${item.id}`}
                        placeholder="Price"
                        value={item.price}
                        onChange={(e) =>
                          handleItemChange(item.id, "price", e.target.value)
                        }
                        className={cx("table-input")}
                        type="number"
                        maxLength={50}
                      />
                    </div>
                    <div className={cx("table-cell", "markup")}>
                      <PrimaryInput
                        name={`markup-${item.id}`}
                        placeholder="Markup%"
                        value={item.markup}
                        onChange={(e) =>
                          handleItemChange(item.id, "markup", e.target.value)
                        }
                        className={cx("table-input")}
                        type="number"
                        maxLength={50}
                      />
                    </div>
                    <div className={cx("table-cell", "line-total")}>
                      <span className={cx("total-value")}>
                        ${calculateLineTotal(item)}
                      </span>
                    </div>
                    <div className={cx("table-cell", "action")}>
                      <button
                        className={cx("delete-button")}
                        onClick={() => handleDeleteItem(item.id)}
                        disabled={laborItems.length === 1}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={cx("add-item-button-container")}>
              <PrimaryButton
                title="Add item"
                onClick={handleAddItem}
                className={cx("add-item-button")}
                icon={<PlusIcon className={cx("plus-icon")} />}
                iconPosition="left"
              />
            </div>
          </div>

          <div className={cx("actions")}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={handleImageUpload}
            />
            <PrimaryButton
              title={`Add Picture${existingImages.length + images.length > 0
                ? ` (${existingImages.length + images.length})`
                : ""
                }`}
              onClick={() => fileInputRef.current?.click()}
              icon={<PlusIcon className={cx("plus-icon")} />}
              iconPosition="left"
              className={cx("action-button")}
            />

            <input
              ref={attachmentInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.csv"
              multiple
              style={{ display: "none" }}
              onChange={handleAttachmentUpload}
            />
            <PrimaryButton
              title={`Add Attachments${existingAttachments.length + attachments.length > 0
                ? ` (${existingAttachments.length + attachments.length})`
                : ""
                }`}
              onClick={() => attachmentInputRef.current?.click()}
              icon={<PlusIcon className={cx("plus-icon")} />}
              iconPosition="left"
              className={cx("action-button")}
            />
          </div>

          {/* Display existing images */}
          {existingImages.length > 0 && (
            <div className={cx("uploaded-files-section")}>
              <h4 className={cx("section-subtitle")}>Existing Images</h4>
              <div className={cx("files-grid")}>
                {existingImages.map((image) => (
                  <div key={image.id} className={cx("file-item", "image-item")}>
                    <img
                      src={image.image?.url || ""}
                      alt={image.image?.fieldName || "Image"}
                      className={cx("image-preview")}
                    />
                    <div className={cx("file-info")}>
                      <span className={cx("file-name")}>
                        {image.image?.fieldName || "Image"}
                      </span>
                      {image.image?.size && (
                        <span className={cx("file-size")}>
                          {image.image.size}
                        </span>
                      )}
                    </div>
                    <button
                      className={cx("remove-file-button")}
                      onClick={() => handleRemoveExistingImage(image.id)}
                      type="button"
                    >
                      <XIcon className={cx("remove-icon")} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Display uploaded images */}
          {images.length > 0 && (
            <div className={cx("uploaded-files-section")}>
              <h4 className={cx("section-subtitle")}>New Uploaded Images</h4>
              <div className={cx("files-grid")}>
                {images.map((image, index) => (
                  <div key={index} className={cx("file-item", "image-item")}>
                    <img
                      src={URL.createObjectURL(image)}
                      alt={`Preview ${index + 1}`}
                      className={cx("image-preview")}
                    />
                    <div className={cx("file-info")}>
                      <span className={cx("file-name")}>{image.name}</span>
                      <span className={cx("file-size")}>
                        {(image.size / 1024).toFixed(2)} KB
                      </span>
                    </div>
                    <button
                      className={cx("remove-file-button")}
                      onClick={() => handleRemoveImage(index)}
                      type="button"
                    >
                      <XIcon className={cx("remove-icon")} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Display existing attachments */}
          {existingAttachments.length > 0 && (
            <div className={cx("uploaded-files-section")}>
              <h4 className={cx("section-subtitle")}>Existing Attachments</h4>
              <div className={cx("files-list")}>
                {existingAttachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className={cx("file-item", "attachment-item")}
                  >
                    <div className={cx("file-info")}>
                      <span className={cx("file-name")}>
                        {attachment.file?.fieldName ||
                          attachment.file?.url ||
                          "Attachment"}
                      </span>
                      {attachment.file?.size && (
                        <span className={cx("file-size")}>
                          {formatFileSize(attachment.file.size)}
                        </span>
                      )}
                    </div>
                    <button
                      className={cx("remove-file-button")}
                      onClick={() =>
                        handleRemoveExistingAttachment(attachment.id)
                      }
                      type="button"
                    >
                      <XIcon className={cx("remove-icon")} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Display uploaded attachments */}
          {attachments.length > 0 && (
            <div className={cx("uploaded-files-section")}>
              <h4 className={cx("section-subtitle")}>
                New Uploaded Attachments
              </h4>
              <div className={cx("files-list")}>
                {attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className={cx("file-item", "attachment-item")}
                  >
                    <div className={cx("file-info")}>
                      <span className={cx("file-name")}>{attachment.name}</span>
                      <span className={cx("file-size")}>
                        {formatFileSize(attachment.size)}
                      </span>
                    </div>
                    <button
                      className={cx("remove-file-button")}
                      onClick={() => handleRemoveAttachment(index)}
                      type="button"
                    >
                      <XIcon className={cx("remove-icon")} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={cx("row")}>
            <div className={cx("item")}>
              <h4 className={cx("item-title")}>Special Instructions</h4>
              <textarea
                className={cx("textarea", "h-[120px]")}
                placeholder="Special Instructions"
                name="specialInstructions"
                value={formData.specialInstructions}
                onChange={handleInputChange}
                rows={4}
                maxLength={500}
              />
            </div>

            <div className={cx("item")}>
              <h4 className={cx("item-title")}>Internal Notes</h4>
              <textarea
                className={cx("textarea", "h-[120px]")}
                placeholder="Internal Notes"
                name="internalNote"
                value={formData.internalNote}
                onChange={handleInputChange}
                rows={4}
                maxLength={500}
              />
            </div>
          </div>

          <div className={cx("summary-container")}>
            <div className={cx("summary-item")}>
              <div className={cx("summary-header")}>
                <h3 className={cx("summary-title")}>Summary</h3>
              </div>

              <div className={cx("summary-content")}>
                <div className={cx("left-content")}>
                  <p className={cx("summary-label")}>Material Cost:</p>
                  <p className={cx("summary-label")}>Labor Cost:</p>
                  <p className={cx("summary-label")}>Total Cost:</p>
                  <p className={cx("summary-label")}>Margin:</p>
                </div>
                <div className={cx("right-content")}>
                  <p className={cx("summary-value")}>
                    ${calculateTotalMaterialCost()}
                  </p>
                  <p className={cx("summary-value")}>
                    ${calculateTotalLaborCost()}
                  </p>
                  <p className={cx("summary-value")}>${calculateTotalCost()}</p>
                  <p className={cx("summary-value")}>
                    ${calculateMarginAmount()}
                  </p>
                </div>
              </div>

              <div className={cx("summary-footer")}>
                <p className={cx("footer-label")}>Total Price:</p>
                <p className={cx("footer-value")}>${calculateTotalPrice()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
      <div className={cx("footer")}>
        <PrimaryButton
          title="Cancel"
          onClick={onCancel}
          className={cx("cancel-button")}
          disabled={isSubmitting}
        />
        <PrimaryButton
          title={type === "create" ? "Create Work Order" : "Save Work Order"}
          onClick={handleSubmit}
          className={cx("save-button")}
          loading={isSubmitting}
          disabled={isSubmitting}
        />
      </div>
    </div>
  );
};

export default WorkOrderCreate;
